import type { MeWorkspace } from '@doneverse/shared'
import { Check, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import styles from './WorkspaceSwitcher.module.css'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { founderInitials } from '@/lib/founder-display'

function founderName(ws: MeWorkspace): string {
  return ws.founder?.name?.trim() || ws.founder?.company_name?.trim() || 'Workspace'
}

/**
 * Deterministic HSL fallback color from a string. Keeps the avatar visually
 * stable per founder when no logo/profile picture is set.
 */
function colorForId(id: string): string {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 55%, 45%)`
}

function WorkspaceAvatar({ workspace, size }: { workspace: MeWorkspace; size: 'trigger' | 'row' }) {
  const f = workspace.founder
  const imageUrl = f?.logo_url || f?.profile_picture_url
  const name = founderName(workspace)
  const initials = founderInitials(name)

  if (size === 'trigger') {
    return <span className={styles.triggerInitials}>{initials}</span>
  }

  return (
    <div
      className={styles.avatar}
      style={imageUrl ? undefined : { background: colorForId(f?.id ?? workspace.id) }}
    >
      {imageUrl ? <img src={imageUrl} alt="" className={styles.avatarImage} /> : initials}
    </div>
  )
}

export function WorkspaceSwitcher() {
  const { workspaces, currentWorkspace, setWorkspaceId } = useWorkspace()
  const [open, setOpen] = useState(false)

  // Caller (IconRailLayout) decides when to mount; we render unconditionally
  // here so this component stays a dumb view.
  const current = currentWorkspace ?? workspaces[0]
  if (!current) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={styles.trigger}
          aria-label="Switch workspace"
          data-testid="workspace-switcher-trigger"
        >
          <WorkspaceAvatar workspace={current} size="trigger" />
          <ChevronRight className={styles.triggerChevron} strokeWidth={2.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={4}
        className={styles.panel}
        data-testid="workspace-switcher-panel"
      >
        <div className={styles.panelHeader}>Switch Workspace</div>
        <div className={styles.list} role="listbox" aria-label="Workspaces">
          {workspaces.map((ws) => {
            const active = ws.id === current.id
            const name = founderName(ws)
            const subtitle = ws.founder?.company_name?.trim() || null
            return (
              <button
                key={ws.id}
                type="button"
                role="option"
                aria-selected={active}
                data-active={active}
                className={styles.row}
                onClick={() => {
                  setWorkspaceId(ws.id)
                  setOpen(false)
                }}
              >
                <WorkspaceAvatar workspace={ws} size="row" />
                <div className={styles.rowText}>
                  <span className={styles.rowName}>{name}</span>
                  {subtitle && <span className={styles.rowSubtitle}>{subtitle}</span>}
                </div>
                <Check
                  className={`${styles.rowCheck} ${active ? '' : styles.rowCheckHidden}`}
                  strokeWidth={3}
                  aria-hidden={!active}
                />
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
